/**
 * ParserHealth · Sprint 10 · Admin-only diagnostics.
 *
 * Read-only projection of /api/parser-health. When more document
 * parsers ship (Parkland, Federated, Shell), each row here shows
 * whether that parser is behaving as expected without digging through
 * logs.
 */
import { useEffect, useState } from "react";
import { Robot, CheckCircle, Warning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EmptyState from "@/components/common/EmptyState";

const fmtPct = (v) => v == null ? "—" : `${(v * 100).toFixed(1)}%`;
const fmtInt = (v) => v == null ? "—" : Number(v).toLocaleString();
const fmtDur = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function ParserHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/parser-health");
        setData(data);
      } catch {
        toast.error("Could not load parser health.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading parser health…</div>;
  if (!data) return null;

  return (
    <div className="p-6 space-y-6" data-testid="parser-health-page">
      <header>
        <div className="text-xs text-slate-500 uppercase tracking-wide">v1.3 · Sprint 10 · Admin diagnostics</div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Parser Health</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          One row per document parser. Numbers reflect the last {data.window.days} days.
        </p>
      </header>

      {data.parsers.length === 0 ? (
        <EmptyState
          icon={Robot}
          title="No parsers registered."
          why="Parsers register themselves at backend boot."
          next="Restart the backend and check the receiving router logs if you expected to see AARAY or Suncor here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {data.parsers.map((p) => <ParserCard key={p.parser} p={p} />)}
        </div>
      )}
    </div>
  );
}

function ParserCard({ p }) {
  const healthy = (p.failure_rate ?? 0) < 0.05 && (p.exception_rate ?? 0) < 0.3;
  return (
    <section
      data-testid={`parser-health-${p.parser}`}
      className={`rounded-lg border p-4 ${healthy ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}
    >
      <header className="flex items-baseline gap-3 flex-wrap mb-3">
        <Robot size={18} className="text-slate-600 self-center" />
        <h2 className="text-base font-semibold text-slate-900">{p.parser}</h2>
        <span className="text-[11px] text-slate-500">v{p.version}</span>
        <span className={[
          "ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
          healthy
            ? "border-emerald-300 bg-emerald-100 text-emerald-800"
            : "border-amber-300 bg-amber-100 text-amber-800",
        ].join(" ")}>
          {healthy ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
          {healthy ? "Healthy" : "Watch"}
        </span>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Cell label="Runs (30d)"          value={fmtInt(p.total_runs)} />
        <Cell label="Last successful run" value={fmtDT(p.last_successful_run)} testid={`${p.parser}-last-run`} />
        <Cell label="Avg AI confidence"   value={fmtPct(p.average_confidence)} />
        <Cell label="Failure rate"        value={fmtPct(p.failure_rate)} />
        <Cell label="Exception rate"      value={fmtPct(p.exception_rate)} />
        <Cell label="Avg processing"      value={fmtDur(p.average_processing_seconds)} />
        <Cell label="Duplicates caught"   value={fmtInt(p.duplicate_detections)} />
        {p.open_exceptions != null && (
          <Cell label="Open exceptions"   value={fmtInt(p.open_exceptions)} />
        )}
      </div>
    </section>
  );
}

function Cell({ label, value, testid }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold tabular-nums text-slate-900 mt-0.5 truncate">{value}</div>
    </div>
  );
}
